import type { Entity, Persisted } from '@tunasong/schemas'
import type { UseChildren } from './graph-types.js'
import { includeElement, isTaggedSysEntity } from '@tunasong/models'

/**
 * Render a `useEntityChild`/`useEntityChildren` filter into a string for telemetry.
 *
 * `JSON.stringify` returns `undefined` for function values, which produced the
 * useless "filter: undefined" Sentry messages in TUNASONG-VC. Prefer the
 * `EntityType` string when present, then the function's `name`, then a
 * placeholder so the resulting message is always grep-able.
 */
export const describeChildFilter = (filter: UseChildren['filter']): string => {
  if (typeof filter === 'string') {
    return filter
  }
  if (typeof filter === 'function') {
    return filter.name || '(anonymous filter)'
  }
  return '(no filter)'
}

/**
 * Render the duplicate children for telemetry. Only the id and type are
 * included so we never leak entity content into Sentry.
 */
export const summarizeDuplicateChildren = (children: Pick<Persisted<Entity>, 'id' | 'type'>[]): string =>
  children.map(({ id, type }) => `${type}#${id}`).join(', ')

/**
 * Pick the canonical child when a filter matched more than one. `useEntityChild`
 * expects a singleton but data races can create duplicates (e.g. a `usersettings`
 * entity created twice by concurrent mounts — GH #1635). Returns the OLDEST by
 * `createdAt` (the original), with a stable tiebreak on `id`, so every caller
 * agrees on the same one regardless of server result order — otherwise settings
 * could flap between the duplicates. 0 or 1 children pass through unchanged.
 */
export const pickCanonicalChild = <T extends Persisted<Entity>>(children: T[]): T | undefined => {
  if (children.length <= 1) {
    return children[0]
  }
  return [...children].sort((a, b) => {
    const ac = a.createdAt ?? ''
    const bc = b.createdAt ?? ''
    if (ac !== bc) {
      return ac < bc ? -1 : 1
    }
    return a.id < b.id ? -1 : 1
  })[0]
}

/**
 * The newest of an API candidate and its cache entry. A `pending` cache entry holds unconfirmed local changes on
 * top of the server version it was edited from, so it also wins a tie on `updatedAt`. GH #1913
 */
export const pickLatest = <T extends Persisted<unknown>>(
  candidate: T | null | undefined,
  cacheEntry: T | undefined,
  pending = false,
  /** The entry carries local patches no save has confirmed; the store rebases newer server versions under them. GH #1966 */
  unconfirmed = false
) => {
  if (!cacheEntry) {
    return candidate
  }
  if (!candidate || unconfirmed) {
    return cacheEntry
  }
  if (cacheEntry.updatedAt > candidate.updatedAt) {
    return cacheEntry
  }
  if (pending && cacheEntry.updatedAt === candidate.updatedAt) {
    return cacheEntry
  }
  return candidate
}

export const getLatestEntry = <T extends Persisted<unknown>>({
  id,
  candidate,
  cache,
  pendingIds,
}: {
  id?: string | null
  cache: Map<string, T>
  // The candidate from the API - this may be outdated
  candidate?: T | null
  pendingIds?: ReadonlySet<string>
}) => (id ? pickLatest(candidate, cache.get(id), pendingIds?.has(id)) : undefined)

export const childFilter =
  ({ parentId, filter: type, includeSys }: UseChildren) =>
  (child: Entity) =>
    child &&
    child.parentId === parentId &&
    includeElement(child, type) &&
    !child.trash &&
    (includeSys ? true : !isTaggedSysEntity(child))

/**
 * Shape the result of an incremental child-entity page request. The lazy RTK Query
 * trigger's `unwrap()` can resolve to `undefined` when a request is aborted or its
 * cache entry is unsubscribed mid-flight (e.g., rapid parentId changes). Treat that
 * as an empty page rather than letting downstream code throw on `undefined.entities`.
 *
 * See Sentry TUNASONG-VB / TUNASONG-V6.
 */
export const shapeChildEntitiesPage = <T extends Entity>(
  data: { entities?: Persisted<Entity>[]; resumeKey?: string | null } | undefined,
  filterArgs: UseChildren
): { entities: T[]; resumeKey?: string | null } => ({
  ...data,
  entities: (data?.entities ?? []).filter(childFilter(filterArgs)) as unknown as T[],
})
